import { makeAutoObservable } from 'mobx'
import type { VisitorsReportItem } from '@/api/reportsApi'
import { i18nStore } from '@store/I18nStore'
import { authStore } from '@store/AuthStore'
import { OperatorOwnPresenceStore } from '@store/OperatorOwnPresenceStore'
import { OperatorWorkspaceSettingsStore } from '@store/OperatorWorkspaceSettingsStore'
import { UserWidgetsStore } from '@store/UserWidgetsStore'
import { OperatorTransfersStore } from '@store/OperatorTransfersStore'
import { billingAccessStore } from '@store/BillingAccessStore'
import { emailVerificationStore } from '@store/EmailVerificationStore'
import { registerBillingAccess } from '@utils/apiHelpers'
import {
  mergeVisitorFeedItems,
  nextVisitorsActiveNow,
  type VisitorsFeedEvent,
} from '@utils/visitorsActiveNow'

export class UIStore {
  theme: 'dark' | 'light' = 'dark'
  counter = 0
  /** WebSocket (Centrifuge) issues by source; site-wide banner if any */
  wsConnectionIssueBySource: { counters: boolean; dialogs: boolean } = {
    counters: false,
    dialogs: false,
  }
  /** Banner state for site-wide websocket connectivity feedback. */
  wsSiteConnectionBannerKind: 'hidden' | 'problem' | 'restored' = 'hidden'
  private wsSiteConnectionBannerTimerId: number | null = null
  /** Unread messages count from dialogs */
  unreadDialogsCount = 0
  /** Accepted ("my") dialogs with a visitor message the operator has not opened yet (tab icon badge). */
  attentionMyDialogsCount = 0
  /** Inbox dialogs count (for sidebar badge) */
  inboxDialogsCount = 0
  /** "My" dialogs count (for dialogs tabs) */
  myDialogsCount = 0
  /** Archived dialogs count (for dialogs tabs) */
  archiveDialogsCount = 0
  /** Active visitors now (sidebar badge + visitors page; from WS visitors.feed) */
  visitorsActiveNow: number | null = null
  /** Last time the active-now count was written (WS / REST / other tab). */
  visitorsActiveNowAt = 0
  /** Bumped on every visitors.feed event so /visitors can refetch the table. */
  visitorsFeedRevision = 0
  /** Live overlay from visitors.feed (merged onto the REST report). */
  visitorsFeedItems: VisitorsReportItem[] | null = null
  /** Flag to avoid double-loading dialogs counters (StrictMode, remounts) */
  dialogsCountersLoadedOnce = false

  constructor() {
    makeAutoObservable(this)
  }

  /** Full-viewport banner on operator site when realtime connection changes. */
  get wsSiteConnectionBannerVisible(): boolean {
    return this.wsSiteConnectionBannerKind !== 'hidden'
  }

  setWsConnectionIssue(source: 'counters' | 'dialogs', hasIssue: boolean) {
    // Site banner is for browser network loss only — ignore Centrifuge reconnect blips while online.
    if (hasIssue && typeof navigator !== 'undefined' && navigator.onLine) {
      return
    }

    const hadAnyIssue = this.wsConnectionIssueBySource.counters || this.wsConnectionIssueBySource.dialogs
    this.wsConnectionIssueBySource[source] = hasIssue
    const hasAnyIssue = this.wsConnectionIssueBySource.counters || this.wsConnectionIssueBySource.dialogs

    if (hasAnyIssue) {
      this.setWsSiteConnectionBanner('problem')
      return
    }

    if (hadAnyIssue && !hasAnyIssue) {
      this.showWsRestoredBannerTemporarily()
    }
  }

  /** Clear all WS issue flags after browser network is back. */
  clearWsConnectionIssuesOnNetworkOnline() {
    const hadAnyIssue = this.wsConnectionIssueBySource.counters || this.wsConnectionIssueBySource.dialogs
    this.wsConnectionIssueBySource.counters = false
    this.wsConnectionIssueBySource.dialogs = false
    if (hadAnyIssue) {
      this.showWsRestoredBannerTemporarily()
    }
  }

  clearWsConnectionIssues() {
    this.wsConnectionIssueBySource.counters = false
    this.wsConnectionIssueBySource.dialogs = false
    this.setWsSiteConnectionBanner('hidden')
  }

  private setWsSiteConnectionBanner(kind: 'hidden' | 'problem' | 'restored') {
    if (this.wsSiteConnectionBannerTimerId !== null) {
      window.clearTimeout(this.wsSiteConnectionBannerTimerId)
      this.wsSiteConnectionBannerTimerId = null
    }
    this.wsSiteConnectionBannerKind = kind
  }

  private showWsRestoredBannerTemporarily() {
    this.setWsSiteConnectionBanner('restored')
    this.wsSiteConnectionBannerTimerId = window.setTimeout(() => {
      this.wsSiteConnectionBannerKind = 'hidden'
      this.wsSiteConnectionBannerTimerId = null
    }, 5_000)
  }

  setUnreadDialogsCount(value: number) {
    this.unreadDialogsCount = Math.max(0, Math.trunc(value))
  }

  setAttentionMyDialogsCount(value: number) {
    this.attentionMyDialogsCount = Math.max(0, Math.trunc(value))
  }

  setInboxDialogsCount(value: number) {
    this.inboxDialogsCount = Math.max(0, Math.trunc(value))
  }

  setMyDialogsCount(value: number) {
    this.myDialogsCount = Math.max(0, Math.trunc(value))
  }

  setArchiveDialogsCount(value: number) {
    this.archiveDialogsCount = Math.max(0, Math.trunc(value))
  }

  setVisitorsActiveNow(value: number) {
    this.visitorsActiveNow = Math.max(0, Math.trunc(value))
    this.visitorsActiveNowAt = Date.now()
  }

  applyVisitorsFeedEvent(event: VisitorsFeedEvent) {
    this.visitorsActiveNow = nextVisitorsActiveNow(
      this.visitorsActiveNow,
      event,
      this.visitorsFeedItems,
    )
    this.visitorsActiveNowAt = Date.now()
    this.visitorsFeedItems = mergeVisitorFeedItems(this.visitorsFeedItems, event)
    this.visitorsFeedRevision += 1
  }

  clearVisitorsActiveNow() {
    this.visitorsActiveNow = null
    this.visitorsActiveNowAt = 0
    this.visitorsFeedRevision = 0
    this.visitorsFeedItems = null
  }

  setDialogsCountersLoadedOnce(value: boolean) {
    this.dialogsCountersLoadedOnce = value
  }

  toggleTheme() {
    this.theme = this.theme === 'dark' ? 'light' : 'dark'
  }

  inc() {
    this.counter++
  }
  dec() {
    this.counter--
  }
}

export class Store {
  ui = new UIStore()
  operatorPresence = new OperatorOwnPresenceStore()
  operatorWorkspaceSettings = new OperatorWorkspaceSettingsStore()
  userWidgets = new UserWidgetsStore()
  operatorTransfers = new OperatorTransfersStore()
  billing = billingAccessStore
  emailVerification = emailVerificationStore
  i18n = i18nStore
  auth = authStore
}

export const store = new Store()

// Session-scoped stores are reset by the auth store on logout / user change, and apiHelpers
// gets the billing store handed over — both wired here so none of these modules has to import
// another one back (no circular / dynamic imports).
authStore.onSessionReset(() => {
  billingAccessStore.reset()
  emailVerificationStore.reset()
})
registerBillingAccess(billingAccessStore)
